import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

// Home stays eager — it's the LCP-critical landing page
import Home from "@/pages/Home";

// All other routes are code-split so the initial JS bundle stays small.
// This cuts Total Blocking Time + main-thread work on Lighthouse mobile.
const Services = lazy(() => import("@/pages/Services"));
const About = lazy(() => import("@/pages/About"));
const AISolutions = lazy(() => import("@/pages/AISolutions"));
const AISolutionDetail = lazy(() => import("@/pages/AISolutionDetail"));
const Results = lazy(() => import("@/pages/Results"));
const CaseStudyDetail = lazy(() => import("@/pages/CaseStudyDetail"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const GrowthAudit = lazy(() => import("@/pages/GrowthAudit"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Refund = lazy(() => import("@/pages/Refund"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));

function ScrollToTopOnRouteChange() {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) return; // allow hash-anchor scroll
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname, hash]);
    return null;
}

function NotFound() {
    return (
        <div className="container-page py-32 text-center">
            <div className="overline">/ 404</div>
            <h1 className="font-display text-6xl md:text-8xl font-bold tracking-tight mt-5 text-[color:var(--ink)]">
                Lost <span className="brand-emph">in the funnel</span>.
            </h1>
            <p className="text-[color:var(--text-muted)] mt-6 text-lg">
                That page doesn&apos;t exist. Try the home page.
            </p>
            <a href="/" className="btn-primary inline-flex mt-8">
                Go home
            </a>
        </div>
    );
}

function RouteFallback() {
    // Minimal, zero-CLS placeholder while the lazy chunk loads.
    // Reserves vertical space so layout doesn't jump.
    return (
        <div
            aria-hidden="true"
            style={{ minHeight: "70vh" }}
            data-testid="route-loading"
        />
    );
}

function PageWithLayout({ children }) {
    return <PublicLayout>{children}</PublicLayout>;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <ScrollToTopOnRouteChange />
                <Suspense fallback={<RouteFallback />}>
                    <Routes>
                        <Route path="/" element={<PageWithLayout><Home /></PageWithLayout>} />
                        <Route path="/services" element={<PageWithLayout><Services /></PageWithLayout>} />
                        <Route path="/about" element={<PageWithLayout><About /></PageWithLayout>} />
                        <Route path="/ai-solutions" element={<PageWithLayout><AISolutions /></PageWithLayout>} />
                        <Route path="/ai-solutions/:slug" element={<PageWithLayout><AISolutionDetail /></PageWithLayout>} />
                        <Route path="/results" element={<PageWithLayout><Results /></PageWithLayout>} />
                        <Route path="/results/:slug" element={<PageWithLayout><CaseStudyDetail /></PageWithLayout>} />
                        <Route path="/blog" element={<PageWithLayout><Blog /></PageWithLayout>} />
                        <Route path="/blog/:slug" element={<PageWithLayout><BlogDetail /></PageWithLayout>} />
                        <Route path="/contact" element={<PageWithLayout><Contact /></PageWithLayout>} />
                        <Route path="/growth-audit" element={<PageWithLayout><GrowthAudit /></PageWithLayout>} />
                        <Route path="/thank-you" element={<PageWithLayout><ThankYou /></PageWithLayout>} />
                        <Route path="/privacy" element={<PageWithLayout><Privacy /></PageWithLayout>} />
                        <Route path="/terms" element={<PageWithLayout><Terms /></PageWithLayout>} />
                        <Route path="/refund" element={<PageWithLayout><Refund /></PageWithLayout>} />

                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute>
                                    <AdminDashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="*" element={<PageWithLayout><NotFound /></PageWithLayout>} />
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </AuthProvider>
    );
}
